import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser } from "../store/slices/authSlice";
import AuthModal from "../components/auth/AuthModal";
import "./Landing.scss";
import Packages from "./Packages";
import { FaUserCircle } from "react-icons/fa";

function Landing() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState("login");
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dispatch = useDispatch();

  // Store the package that user was trying to purchase
  const pendingPurchaseRef = useRef(null);

  const openLoginModal = (packageData = null) => {
    // If packageData is provided, store it for after authentication
    if (packageData) {
      pendingPurchaseRef.current = packageData;
    }
    setAuthModalView("login");
    setIsAuthModalOpen(true);
  };

  const openRegisterModal = () => {
    setAuthModalView("register");
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  // This will be called after successful authentication
  const handleAuthSuccess = () => {
    // If there was a pending purchase, trigger it now
    if (pendingPurchaseRef.current) {
      const { packageItem, quantity, totalPrice, onSelect } =
        pendingPurchaseRef.current;
      // Clear the pending purchase
      pendingPurchaseRef.current = null;
      // Execute the purchase
      onSelect(packageItem, quantity, totalPrice);
    }
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      setUserMenuOpen(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-container">
        <header className="landing-header">
          <div className="logo">FredoCloud</div>
          <nav className="landing-nav">
            <ul>
              <li>
                <a href="https://fredocloud.com">Main Site</a>
              </li>
              <li>
                <a href="https://fredocloud.com/about">About</a>
              </li>
              <li>
                <a href="https://fredocloud.com/contact">Contact</a>
              </li>
            </ul>
          </nav>

          <div className="header-right">
            {isAuthenticated ? (
              <div className="user-menu-container">
                <button className="user-menu-button" onClick={toggleUserMenu}>
                  <FaUserCircle />
                  <span>{user?.name || user?.email || "User"}</span>
                </button>

                {userMenuOpen && (
                  <div className="user-dropdown">
                    <Link to="/dashboard" className="dropdown-item">
                      Dashboard
                    </Link>
                    <Link to="/profile" className="dropdown-item">
                      Profile
                    </Link>
                    <button onClick={handleLogout} className="dropdown-item">
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </header>

        <Packages
          isAuthenticated={isAuthenticated}
          openLoginModal={openLoginModal}
          pendingPurchaseRef={pendingPurchaseRef}
        />

        <section className="features-section">
          <h2>Why Use Our Client Portal?</h2>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Manage Services</h3>
              <p>
                View and manage all your active web development services in one
                place.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">💳</div>
              <h3>Simple Billing</h3>
              <p>
                Track invoices, payment history, and manage payment methods
                easily.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure Access</h3>
              <p>
                Your data is always protected with our secure authentication
                system.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Project Tracking</h3>
              <p>
                Monitor the progress of your web development projects in real
                time.
              </p>
            </div>
          </div>
        </section>

        <footer className="landing-footer">
          <p>© 2024 FredoCloud. All rights reserved.</p>
        </footer>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        initialView={authModalView}
        onAuthSuccess={handleAuthSuccess}
      />
    </div>
  );
}

export default Landing;
